import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  Team,
  TeamMember,
  TeamInvitation,
  TeamRole,
  CreateTeamRequest,
  UpdateTeamRequest,
  InviteToTeamRequest,
  AcceptInvitationRequest,
  UpdateTeamMemberRoleRequest,
  TransferOwnershipRequest,
  TeamResponse,
} from '@spectrace/shared';

@Injectable({
  providedIn: 'root',
})
export class TeamService {
  private apiUrl = `${environment.apiUrl}/teams`;

  constructor(private http: HttpClient) {}

  /**
   * Get all teams for the authenticated user
   */
  getUserTeams(): Observable<{ teams: Team[] }> {
    return this.http.get<{ teams: Team[] }>(this.apiUrl);
  }

  /**
   * Get team details with members
   */
  getTeamDetails(teamId: string): Observable<TeamResponse> {
    return this.http.get<TeamResponse>(`${this.apiUrl}/${teamId}`);
  }

  /**
   * Create a new team
   */
  createTeam(request: CreateTeamRequest): Observable<{ team: Team }> {
    return this.http.post<{ team: Team }>(this.apiUrl, request);
  }

  /**
   * Update team details
   */
  updateTeam(
    teamId: string,
    request: UpdateTeamRequest,
  ): Observable<{ team: Team }> {
    return this.http.put<{ team: Team }>(`${this.apiUrl}/${teamId}`, request);
  }

  /**
   * Invite user to team
   */
  inviteToTeam(
    teamId: string,
    request: InviteToTeamRequest,
  ): Observable<{ invitation: TeamInvitation }> {
    return this.http.post<{ invitation: TeamInvitation }>(
      `${this.apiUrl}/${teamId}/invite`,
      request,
    );
  }

  /**
   * Create a generic invite token for a team
   */
  createInviteToken(
    teamId: string,
  ): Observable<{ invitation: TeamInvitation }> {
    return this.http.post<{ invitation: TeamInvitation }>(
      `${this.apiUrl}/${teamId}/invite-token`,
      {},
    );
  }

  /**
   * Get pending invitations
   */
  getPendingInvitations(): Observable<{ invitations: TeamInvitation[] }> {
    return this.http.get<{ invitations: TeamInvitation[] }>(
      `${this.apiUrl}/invitations/pending`,
    );
  }

  /**
   * Accept team invitation
   */
  acceptInvitation(
    request: AcceptInvitationRequest,
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/invitations/accept`,
      request,
    );
  }

  /**
   * Remove team member
   */
  removeMember(
    teamId: string,
    userId: string,
  ): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${this.apiUrl}/${teamId}/members/${userId}`,
    );
  }

  /**
   * Get team members
   */
  getTeamMembers(teamId: string): Observable<{ members: TeamMember[] }> {
    return this.http.get<{ members: TeamMember[] }>(
      `${this.apiUrl}/${teamId}/members`,
    );
  }

  updateMemberRole(
    teamId: string,
    userId: string,
    role: Exclude<TeamRole, 'owner'>,
  ): Observable<{ member: TeamMember }> {
    const body: UpdateTeamMemberRoleRequest = { role };
    return this.http.patch<{ member: TeamMember }>(
      `${this.apiUrl}/${teamId}/members/${userId}/role`,
      body,
    );
  }

  transferOwnership(
    teamId: string,
    newOwnerUserId: string,
  ): Observable<{ success: boolean }> {
    const body: TransferOwnershipRequest = { newOwnerUserId };
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/${teamId}/transfer-ownership`,
      body,
    );
  }

  getTeamInvitations(
    teamId: string,
  ): Observable<{ invitations: TeamInvitation[] }> {
    return this.http.get<{ invitations: TeamInvitation[] }>(
      `${this.apiUrl}/${teamId}/invitations`,
    );
  }

  revokeInvitation(
    teamId: string,
    invitationId: string,
  ): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${this.apiUrl}/${teamId}/invitations/${invitationId}`,
    );
  }
}
